<script setup lang="ts">
  import { computed } from 'vue'
  // Postal/visit/billing address sections share the header +
  // DataContainerAddress + edit-button shell; explicit per-section markup is
  // preferred over a data-driven wrapper. See feedback_primitives_over_wrappers.
  // fallow-ignore-next-line code-duplication
  import { isStakeholderClient, testIds, type Client } from '@gat-ship/types'
  import { text } from '@gat-ship/stakeholders'
  import { IconLoading } from '@gat-ship/icons'
  import {
    ModalSection,
    ModalSectionHeader,
    ModalSectionHeaderTitle,
    Button
  } from '@gat-ship/vue-components'
  import { DataContainerAddress } from '@gat-ship/ref-data'
  import { useStakeholderContext } from '../../../composables/useStakeholder'
  import { useStakeholderModals } from '../../providers/stakeholder-modals'
  import {
    useStakeholderEditAction,
    type StakeholderEditButtonConfig
  } from '../../../composables/useStakeholderEditAction'

  const service = useStakeholderContext()
  const modals = useStakeholderModals()

  const client = computed<Client | undefined>(() => {
    const value = service?.stakeholder.value
    return value && isStakeholderClient(value) ? value : undefined
  })

  const { isLoading, canShow, btnConfig } = useStakeholderEditAction(
    () => client.value,
    () =>
      text.groups.billingAddress(client.value?.stakeholderType ?? 'client')
        .buttons.changeBillingAddress as StakeholderEditButtonConfig
  )
</script>

<template>
  <ModalSection v-if="client">
    <ModalSectionHeader>
      <ModalSectionHeaderTitle>
        {{ text.groups.billingAddress(client.stakeholderType).title }}
      </ModalSectionHeaderTitle>
    </ModalSectionHeader>
    <span v-if="client.billingAddressSameAsPostal" class="text-sm"
      >Same as post address</span
    >
    <DataContainerAddress
      :class="{
        'text-muted-foreground': client.billingAddressSameAsPostal
      }"
      :data="{
        address: client.billingAddressSameAsPostal
          ? (client.postalAddress ?? undefined)
          : (client.billingAddress ?? undefined)
      }"
      no-data-text="No post address."
    />
    <Button
      v-if="canShow"
      :variant="btnConfig.variant"
      :size="btnConfig.size"
      :class="btnConfig.class"
      :disabled="isLoading"
      :data-testid="testIds.stakeholders.actions.changeBillingAddress"
      @click="modals?.showUpdateBillingAddress({ stakeholder: client })"
    >
      <IconLoading v-if="isLoading" class="size-4 shrink-0" />
      <component
        :is="btnConfig.icon"
        v-else-if="btnConfig.icon"
        class="size-4"
      />
      {{ btnConfig.label }}
    </Button>
  </ModalSection>
</template>
